import { Typography } from '@mui/material';
import { DEFAULT_CURRENCY, type PriceTier } from '@weft/types';
import { isEmptyString, isNotNullOrUndefined } from '@weft/utils';
import cn from 'classnames';
import { useCallback } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { CloseButton } from '../../../../../../../../components/design/buttons/CloseButton';
import { Price } from '../../../../../../../../components/design/Price';
import { useFormErrorNotificationContext } from '../../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { deleteFromTiers } from '../../../../../../../../utils/tiers-modifier';
import type { PlanInputForm } from '../../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import type { UsageProductVariantTableRow } from '../usage-product-variant-table-edit-types/usage-product-variant-table-row';

type EditTiersTablePerUnitColumnProps = {
  value: number | undefined;
  row: UsageProductVariantTableRow;
  isRevShareProduct?: boolean;
  isFlatRateColumn?: boolean;
  isTieredSubscriptionProduct?: boolean;
};

export const EditTiersTablePerUnitColumn: React.FC<EditTiersTablePerUnitColumnProps> = ({
  value, row: {
    index, productVariantIndex, productIndex,
  },
  isRevShareProduct,
  isFlatRateColumn,
  isTieredSubscriptionProduct,
}) => {
  const registerType: 'price' | 'flatRate' = isFlatRateColumn ? 'flatRate' : 'price';
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { getValues, setValue, formState: { errors } } = useFormContext<PlanInputForm>();

  const handleDeleteRow = useCallback(() => {
    const tiers = getValues(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`) as Partial<PriceTier>[];
    const updatedTiers = deleteFromTiers(tiers, index);
    setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`, updatedTiers as PriceTier[]);
  }, [getValues, index, productIndex, productVariantIndex, setValue]);

  const hasError = get(errors, `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}.${registerType}`);

  registerErrorField(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}.${registerType}`, CREATE_PLAN_TABS.tierAndVolumePricing);

  return (
    <div className={cn(
      'group/row relative h-full w-full',
      { 'border-2 border-input-error': hasError },
    )}
    >
      <span className={cn('font-sans justify-center items-center h-full flex text-base text-black', { 'text-gray': isEmptyString(value?.toString()) })}>
        {
          (isRevShareProduct && !isFlatRateColumn ) ? (
            value ? (
              <Typography className={cn('font-sans justify-center items-center h-full flex text-base text-black', { 'text-gray': isEmptyString(value?.toString()) })}>
                {value}
              </Typography>
            ) : '%'
          ) : (
            isNotNullOrUndefined(value) ? (
              <Price
                className={cn('font-sans justify-center items-center h-full flex text-base text-black', { 'text-gray': isEmptyString(value?.toString()) })}
              >
                {value}
              </Price>
            ) : DEFAULT_CURRENCY
          )
        }
      </span>
      {(index > 0 && (!isFlatRateColumn || isTieredSubscriptionProduct)) && (
        <CloseButton
          onClick={handleDeleteRow}
          type="button"
          className='absolute right-0 top-1/2 z-20 -translate-x-2 -translate-y-1/2 opacity-0 group-hover/row:opacity-100'
        />
      )}
    </div>
  );
};